import { useState, useCallback, useRef } from 'react';
import { ChatMessage, UIState, WebSocketControlMessage, Question } from './types';
import { Box } from '@amzn/awsui-components-console';

// Extend Window interface for global WebSocket control state
declare global {
    interface Window {
        __ADDAPT_SETTINGS__?: any;
        __ADDAPT_UPLOADED_FILE__?: File;
        __ADDAPT_PENDING_FILE__?: File;
        __ADDAPT_TEMPLATE__?: any;
        __ADDAPT_STOP_POLLING__?: () => void;
        __ADDAPT_REGENERATE__?: (payload: any) => void;
        __ADDAPT_FEEDBACK_PAYLOAD__?: any;
        __ADDAPT_API_INSTRUCTION__?: any;
        __ADDAPT_CALL_API__?: (endpoint: string, payload: any, nextStep: any) => void;
    }
}

interface UseWebSocketControlProps {
    userName?: string;
}

export const useWebSocketControl = ({ userName = 'there' }: UseWebSocketControlProps = {}) => {
    const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
    const [wsUiState, setWsUiState] = useState<UIState>({
        input_enabled: false,
        input_placeholder: 'Type your message...',
        show_progress: false,
        show_file_upload: false,
        chips: [],
        progress: undefined,
        show_typing: false,
        clear_previous: false,
        disable_all: false
    });
    const [allQuestions, setAllQuestions] = useState<Question[]>([]);  // Store all questions for new protocol
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);  // Show questionnaire form
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);  // Track current question
    const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});  // Store answers
    // Generate session ID on component mount
    const [wsSessionId, setWsSessionId] = useState<string>(() => {
        const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return sessionId;
    });
    const [wsControlConnected, setWsControlConnected] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);

    // Forward declare showNextQuestion
    const showNextQuestionRef = useRef<(question: Question, number: number, total: number) => void>();

    // Helper function to show next question
    const showNextQuestion = useCallback((question: Question, number: number, total: number) => {
        // Build question content with clear instructions for select questions
        let questionContent = `**Question ${number} of ${total}**\n\n${question.text}${question.required ? ' *' : ''}`;
        
        // Add instruction for select questions
        if (question.input_type === 'select' && question.options) {
            questionContent += `\n\n📝 _Please select one of the options below:_`;
        }
        
        // Add help text if available
        if (question.help_text) {
            questionContent += `\n\n_${question.help_text}_`;
        }
        
        // Show the question
        const questionMessage: ChatMessage = {
            id: `question-${question.id}`,
            type: 'assistant',
            content: questionContent,
            timestamp: new Date(),
            status: 'sent'
        };
        setLocalMessages(prev => [...prev, questionMessage]);
        
        // Update progress
        setWsUiState(prev => ({
            ...prev,
            show_progress: true,
            progress: {
                current: number,
                total: total,
                percentage: (number / total) * 100,
                label: 'Template Generation Progress',
                description: `Question ${number} of ${total}`
            },
            input_enabled: true,
            input_placeholder: question.input_type === 'select' ? 'Select from options above or type exact match...' : 'Type your answer...',
            chips: question.options ? question.options.map((opt, idx) => ({
                id: `q${question.id}_opt_${idx}`,
                text: opt,
                value: opt,
                action: 'answer_question'
            })) : []
        }));
    }, []);
    
    // Store the function in the ref
    showNextQuestionRef.current = showNextQuestion;
    
    // Handle answer and show next question
    const handleQuestionAnswer = useCallback((questionId: string, answer: string) => {
        
        
        // Store the answer
        setQuestionAnswers(prev => ({ ...prev, [questionId]: answer }));
        
        // Show user's answer
        const userAnswer: ChatMessage = {
            id: `answer-${questionId}-${Date.now()}`,
            type: 'user',
            content: answer,
            timestamp: new Date(),
            status: 'sent'
        };
        setLocalMessages(prev => [...prev, userAnswer]);
        
        // Disable input while processing
        setWsUiState(prev => ({ ...prev, input_enabled: false, chips: [] }));
        
        // Check if there are more questions
        const nextIndex = currentQuestionIndex + 1;
        
        if (nextIndex < allQuestions.length) {
            // Show loading before next question
            setTimeout(() => {
                const loadingMsg: ChatMessage = {
                    id: `loading-${Date.now()}`,
                    type: 'assistant',
                    content: <Box color="text-status-inactive">Generating next question...</Box>,
                    timestamp: new Date(),
                    avatarLoading: true,
                    status: 'sent'
                };
                setLocalMessages(prev => [...prev, loadingMsg]);
                
                // Show next question after loading
                setTimeout(() => {
                    setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                    setCurrentQuestionIndex(nextIndex);
                    showNextQuestion(allQuestions[nextIndex], nextIndex + 1, allQuestions.length);
                }, 1500);
            }, 500);
        } else {
            // All questions answered - submit
            setTimeout(() => {
                const completionMsg: ChatMessage = {
                    id: `completion-${Date.now()}`,
                    type: 'assistant',
                    content: '🎉 Great! I have all the information I need. Let me generate your template...',
                    timestamp: new Date(),
                    status: 'sent'
                };
                setLocalMessages(prev => [...prev, completionMsg]);
                
                // Add loading message
                setTimeout(() => {
                    const loadingMsg: ChatMessage = {
                        id: `loading-${Date.now()}`,
                        type: 'assistant',
                        content: <Box color="text-status-inactive">Generating your custom template...</Box>,
                        timestamp: new Date(),
                        avatarLoading: true,
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, loadingMsg]);
                    
                    // Submit all answers
                    const allAnswers: Record<string, string> = { ...questionAnswers, [questionId]: answer };
                    
                    // Build simple question:answer format using question text
                    const questionAnswerMap: Record<string, string> = {};
                    
                    Object.entries(allAnswers).forEach(([qId, ans]) => {
                        // Skip invalid values
                        if (ans === undefined || ans === null || ans === 'undefined' || ans === 'null') {
                            console.warn(`⚠️ Skipping question ${qId} with invalid value:`, ans);
                            return;
                        }
                        
                        // Find the question object to get the text
                        const question = allQuestions.find(q => q.id === qId);
                        if (question) {
                            // Use question text as key instead of question ID
                            questionAnswerMap[question.text] = ans;
                        }
                    });
                    
                    
                    // Send via WebSocket
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                        const message = {
                            action: 'submit_all_answers',
                            questionAnswers: questionAnswerMap,
                            sessionId: wsSessionId
                        };
                        wsRef.current.send(JSON.stringify(message));
                    }
                }, 1000);
            }, 500);
        }
    }, [allQuestions, currentQuestionIndex, questionAnswers, showNextQuestion, wsSessionId]);
    
    // Handle template status updates
    const handleTemplateStatusUpdate = useCallback((
        status: string | undefined, 
        templateId: string | undefined,
        validationErrors?: Array<{ error_type: string; message: string }>
    ) => {
        // Get the parent component's stopTemplatePolling if available
        const stopPolling = window.__ADDAPT_STOP_POLLING__;
        
        if (status === 'IN_PROGRESS') {
            // Update progress
            setWsUiState(prev => ({
                ...prev,
                show_progress: true,
                progress: {
                    current: 50,
                    total: 100,
                    percentage: 50,
                    label: 'Generating Template',
                    description: `Template ID: ${templateId}`
                }
            }));
        } else if (status === 'VALIDATED') {
            // Stop polling
            if (stopPolling) stopPolling();
            
            // Template ready, ask for job name
            const successMessage: ChatMessage = {
                id: `validated-${Date.now()}`,
                type: 'assistant',
                content: `🎉 Template validated successfully!\n\nTemplate ID: ${templateId}\n\nNow let's create a job with this template. Please provide a name for your job:`,
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages(prev => [...prev, successMessage]);
            
            // Store template ID for job creation
            window.__ADDAPT_TEMPLATE__ = { 
                ...window.__ADDAPT_TEMPLATE__,
                templateId,
                templateUri: '' // Will be set from backend
            };
            
            // Enable input for job name
            setWsUiState(prev => ({
                ...prev,
                show_progress: false,
                input_enabled: true,
                input_placeholder: 'Enter job name (e.g., "Monthly GL Reconciliation")',
                chips: []
            }));
        } else if (status === 'VALIDATION_FAILED') {
            // Stop polling
            if (stopPolling) stopPolling();
            
            // Show validation errors
            let errorContent = `⚠️ Template validation failed:\n\n`;
            if (validationErrors) {
                validationErrors.forEach((error, idx) => {
                    errorContent += `• ${error.error_type}: ${error.message}\n`;
                });
            }
            errorContent += '\nWould you like to provide feedback to fix these issues?';
            
            const errorMessage: ChatMessage = {
                id: `validation-error-${Date.now()}`,
                type: 'assistant',
                content: errorContent,
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages(prev => [...prev, errorMessage]);
            
            // Store template ID for feedback
            window.__ADDAPT_TEMPLATE__ = { 
                ...window.__ADDAPT_TEMPLATE__,
                templateId
            };
            
            // Show feedback options
            setWsUiState(prev => ({
                ...prev,
                show_progress: false,
                input_enabled: true,
                input_placeholder: 'Please provide feedback on how to fix these validation errors...',
                chips: [
                    {
                        id: 'retry_generation',
                        text: '📝 Retry with Feedback',
                        action: 'submit_feedback'
                    },
                    {
                        id: 'start_over',
                        text: '🔄 Start Over',
                        action: 'start_questionnaire'
                    }
                ]
            }));
        }
    }, []);

    // Handle incoming WebSocket messages
    const handleWebSocketMessage = useCallback((data: WebSocketControlMessage) => {
        // Handle action-based messages (new backend protocol)
        if (data.action) {
            switch (data.action) {
                case 'template_in_progress':
                    handleTemplateStatusUpdate('IN_PROGRESS', data.templateId);
                    break;
                    
                case 'template_validation_failed':
                    handleTemplateStatusUpdate('VALIDATION_FAILED', data.templateId, data.validationErrors);
                    break;
                    
                case 'template_validated':
                    handleTemplateStatusUpdate('VALIDATED', data.templateId);
                    if (data.templateUri) {
                        window.__ADDAPT_TEMPLATE__ = {
                            ...window.__ADDAPT_TEMPLATE__,
                            templateUri: data.templateUri
                        };
                    }
                    break;
                    
                case 'template_not_found':
                    // Stop polling
                    const stopPolling = window.__ADDAPT_STOP_POLLING__;
                    if (stopPolling) stopPolling();
                    
                    const notFoundMessage: ChatMessage = {
                        id: `not-found-${Date.now()}`,
                        type: 'system',
                        content: `❌ Template not found: ${data.templateId}`,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, notFoundMessage]);
                    break;
                    
                case 'feedback_received':
                    if (data.apiPayload) {
                        // Store feedback payload for regeneration
                        window.__ADDAPT_FEEDBACK_PAYLOAD__ = data.apiPayload;
                        
                        const feedbackMessage: ChatMessage = {
                            id: `feedback-${Date.now()}`,
                            type: 'system',
                            content: '📝 Feedback received. Regenerating template with your feedback...',
                            timestamp: new Date(),
                            status: 'sent'
                        };
                        setLocalMessages(prev => [...prev, feedbackMessage]);
                        
                        // Trigger regeneration with feedback
                        if (window.__ADDAPT_REGENERATE__) {
                            window.__ADDAPT_REGENERATE__(data.apiPayload);
                        }
                    }
                    break;
            }
            return;
        }
        
        // Handle type-based messages (new and legacy protocol)
        switch (data.type) {
            // NEW PROTOCOL MESSAGES
            case 'welcome':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                if (data.message) {
                    const welcomeMessage: ChatMessage = {
                        id: `welcome-${Date.now()}`,
                        type: 'assistant',
                        content: data.message.content,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, welcomeMessage]);
                }
                if (data.ui_state) {
                    setWsUiState(prev => ({ ...prev, ...data.ui_state }));
                }
                break;
                
            case 'show_all_questions':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                // Clear chips immediately to hide action buttons during questionnaire loading
                setWsUiState(prev => ({
                    ...prev,
                    chips: [],
                    input_enabled: false
                }));
                
                if (data.questions && data.questions.length > 0) {
                    // Store all questions for sequential display
                    setAllQuestions(data.questions);
                    // Don't show questionnaire form - keep chat experience
                    setShowQuestionnaire(false);
                    
                    // Show intro message
                    if (data.message) {
                        const introMessage: ChatMessage = {
                            id: `intro-${Date.now()}`,
                            type: 'assistant',
                            content: data.message.content || "Great! Let me ask you a few questions to generate your custom template.",
                            timestamp: new Date(),
                            status: 'sent'
                        };
                        setLocalMessages(prev => [...prev, introMessage]);
                    }
                    
                    // Add a loading message before showing first question
                    setTimeout(() => {
                        const loadingMsg: ChatMessage = {
                            id: `loading-${Date.now()}`,
                            type: 'assistant',
                            content: <Box color="text-status-inactive">Preparing your questions...</Box>,
                            timestamp: new Date(),
                            avatarLoading: true,
                            status: 'sent'
                        };
                        setLocalMessages(prev => [...prev, loadingMsg]);
                        
                        // Show first question after loading
                        setTimeout(() => {
                            setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                            if (showNextQuestionRef.current && data.questions) {
                                showNextQuestionRef.current(data.questions[0], 1, data.questions.length);
                            }
                        }, 1500);
                    }, 500);
                }
                break;
                
            case 'answers_accepted':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                setShowQuestionnaire(false);
                
                if (data.message) {
                    const acceptedMessage: ChatMessage = {
                        id: `accepted-${Date.now()}`,
                        type: 'assistant',
                        content: data.message.content,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, acceptedMessage]);
                }
                
                // Update UI state to show progress
                if (data.ui_state) {
                    setWsUiState(prev => ({ ...prev, ...data.ui_state }));
                }
                break;
                
            case 'validation_errors':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                if (data.errors) {
                    // Format validation errors
                    let errorContent = '❌ Please fix the following validation errors:\n\n';
                    Object.entries(data.errors).forEach(([questionId, error]) => {
                        const question = allQuestions.find(q => q.id === questionId);
                        errorContent += `• Question ${question?.number || questionId}: ${error}\n`;
                    });
                    
                    const errorMessage: ChatMessage = {
                        id: `validation-errors-${Date.now()}`,
                        type: 'system',
                        content: errorContent,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, errorMessage]);
                }
                
                if (data.message) {
                    const message: ChatMessage = {
                        id: `msg-${Date.now()}`,
                        type: 'assistant',
                        content: data.message.content,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, message]);
                }
                break;
                
            case 'call_rest_api':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                // Store API instruction for parent component
                if (data.apiEndpoint && data.apiPayload) {
                    window.__ADDAPT_API_INSTRUCTION__ = {
                        endpoint: data.apiEndpoint,
                        payload: data.apiPayload,
                        nextStep: data.nextStep
                    };
                    
                    
                    // Trigger API call in parent component
                    if (window.__ADDAPT_CALL_API__) {
                        window.__ADDAPT_CALL_API__(data.apiEndpoint, data.apiPayload, data.nextStep);
                    }
                }
                break;
                
            case 'typing':
                // Show typing indicator
                setWsUiState(prev => ({ ...prev, show_typing: true }));
                
                if (data.message) {
                    const typingMessage: ChatMessage = {
                        id: `typing-${Date.now()}`,
                        type: 'assistant',
                        content: <Box color="text-status-inactive">AI is thinking...</Box>,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, typingMessage]);
                }
                break;
                
            // LEGACY PROTOCOL MESSAGES (for backward compatibility)
            case 'init_chat':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                if (data.message) {
                    const initMessage: ChatMessage = {
                        id: `init-${Date.now()}`,
                        type: 'assistant',
                        content: data.message.content,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, initMessage]);
                }
                if (data.ui_state) {
                    setWsUiState(prev => ({ ...prev, ...data.ui_state }));
                }
                if (data.sessionId) {
                    setWsSessionId(data.sessionId);
                }
                break;
                
            case 'show_question':
                if (data.question) {
                    // Remove any loading messages first
                    setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                    
                    // Show question message
                    const questionMessage: ChatMessage = {
                        id: `question-${data.question.id}`,
                        type: 'assistant',
                        content: data.question.text,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, questionMessage]);
                    
                    // Update progress if provided
                    if (data.progress) {
                        setWsUiState(prev => ({
                            ...prev,
                            show_progress: true,
                            progress: {
                                current: data.progress?.current_question || 0,
                                total: data.progress?.total_questions || 10,
                                percentage: data.progress?.percentage || 0,
                                label: '',
                                description: data.progress_message || `Question ${data.progress?.current_question} of ${data.progress?.total_questions}`
                            }
                        }));
                    }
                    
                    // Set up chips from options
                    if (data.ui_state?.chips) {
                        setWsUiState(prev => ({
                            ...prev,
                            chips: data.ui_state?.chips || []
                        }));
                    } else if (data.question.options) {
                        // Create chips from question options
                        const chips = data.question.options.map((option: string, idx: number) => ({
                            id: `option_${idx}`,
                            text: option,
                            value: option,
                            action: 'select_option'
                        }));
                        
                        // Add skip button if not required
                        if (!data.question.required) {
                            chips.push({
                                id: 'skip_question',
                                text: '⏭️ Skip',
                                value: 'skip',
                                action: 'skip_question'
                            });
                        }
                        
                        setWsUiState(prev => ({
                            ...prev,
                            chips,
                            input_enabled: true,
                            input_placeholder: 'Type your answer or select from options above...'
                        }));
                    }
                }
                break;
                
            case 'template_ready':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                if (data.message) {
                    const readyMessage: ChatMessage = {
                        id: `msg-${Date.now()}`,
                        type: 'assistant',
                        content: data.message.content,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, readyMessage]);
                }
                
                // Store template info for download
                if (data.template) {
                    setWsSessionId(data.template.template_id);
                }
                
                // Update UI state
                if (data.ui_state) {
                    setWsUiState(prev => ({ ...prev, ...data.ui_state }));
                }
                break;
                
            case 'progress_update':
                if (data.progress) {
                    setWsUiState(prev => ({
                        ...prev,
                        show_progress: true,
                        progress: {
                            current: data.progress?.percentage || 0,
                            total: 100,
                            percentage: data.progress?.percentage || 0,
                            label: 'Processing',
                            description: data.progress?.message || 'Please wait...'
                        }
                    }));
                }
                break;
                
            case 'validation_error':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                if (data.error) {
                    const errorMessage: ChatMessage = {
                        id: `error-${Date.now()}`,
                        type: 'system',
                        content: `❌ Validation Error: ${data.error.message}`,
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, errorMessage]);
                }
                if (data.ui_state) {
                    setWsUiState(prev => ({ ...prev, ...data.ui_state }));
                }
                break;
                
            case 'message':
                if (data.message) {
                    // Remove any loading messages first
                    setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                    
                    const message: ChatMessage = {
                        id: `msg-${Date.now()}`,
                        type: data.message.role === 'user' ? 'user' : 'assistant',
                        content: data.message.content,
                        timestamp: data.message.timestamp ? new Date(data.message.timestamp) : new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, message]);
                }
                break;
                
            case 'error':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                // Extract error message properly
                let errorText = 'An error occurred';
                if (data.message) {
                    // Check if message is an object with content property
                    if (typeof data.message === 'object' && data.message.content) {
                        errorText = data.message.content;
                    } else if (typeof data.message === 'string') {
                        errorText = data.message;
                    }
                } else if (data.error) {
                    if (typeof data.error === 'string') {
                        errorText = data.error;
                    } else if (data.error.message) {
                        errorText = data.error.message;
                    }
                }
                
                const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}`,
                    type: 'system',
                    content: errorText,
                    timestamp: new Date(),
                    status: 'sent'
                };
                setLocalMessages(prev => [...prev, errorMessage]);
                
                // Log full error for debugging
                console.error('WebSocket error received:', data);
                
                // Reset UI state on error
                setWsUiState(prev => ({
                    ...prev,
                    input_enabled: false,
                    show_progress: false,
                    chips: [
                        {
                            id: 'try_again',
                            text: '🔄 Try Again',
                            action: 'start_over'
                        }
                    ]
                }));
                break;
                
            case 'request_file_upload':
                setWsUiState(prev => ({
                    ...prev,
                    show_file_upload: true,
                    input_enabled: false,
                    input_placeholder: 'Upload your Excel file above'
                }));
                break;
                
            case 'questionnaire_complete':
                // Remove any loading messages first
                setLocalMessages(prev => prev.filter(msg => !msg.id.startsWith('loading-')));
                
                // Questionnaire completed, ready to generate template
                if (data.answers) {
                    const completeMessage: ChatMessage = {
                        id: `complete-${Date.now()}`,
                        type: 'assistant',
                        content: '✅ All questions answered! Generating your template...',
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages(prev => [...prev, completeMessage]);
                    
                    // Store answers for template generation
                    window.__ADDAPT_TEMPLATE__ = { answers: data.answers };
                    
                    // Update UI state
                    setWsUiState(prev => ({
                        ...prev,
                        input_enabled: false,
                        show_progress: true,
                        progress: {
                            current: 0,
                            total: 100,
                            percentage: 0,
                            label: 'Generating Template',
                            description: 'Initializing...'
                        },
                        chips: [{
                            id: 'generate_template',
                            text: '🚀 Generate Template',
                            action: 'generate_template'
                        }]
                    }));
                }
                break;
                
            case 'template_status':
                // Legacy template status handling (if backend still sends this)
                if (data.templateStatus) {
                    const status = data.templateStatus;
                    handleTemplateStatusUpdate(status.status, status.templateId, status.validationErrors);
                }
                break;
                
            case 'request_job_name':
                // Request job name for job creation
                const jobNameMessage: ChatMessage = {
                    id: `job-name-${Date.now()}`,
                    type: 'assistant',
                    content: 'Please provide a name for your job:',
                    timestamp: new Date(),
                    status: 'sent'
                };
                setLocalMessages(prev => [...prev, jobNameMessage]);
                
                setWsUiState(prev => ({
                    ...prev,
                    input_enabled: true,
                    input_placeholder: 'Enter job name...',
                    chips: []
                }));
                break;
        }
    }, [handleTemplateStatusUpdate]);

    // Reset to initial state
    const resetChat = useCallback((clearDraftJob: boolean = false) => {
        // Generate a new session ID for fresh start
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setWsSessionId(newSessionId);
        
        // Clear template state
        if (window.__ADDAPT_TEMPLATE__) {
            if (clearDraftJob) {
                // Complete reset - delete everything including jobId
                delete window.__ADDAPT_TEMPLATE__;
            } else {
                // Soft reset - preserve jobId and createdAt from draft job
                const preservedJobId = window.__ADDAPT_TEMPLATE__.jobId;
                const preservedCreatedAt = window.__ADDAPT_TEMPLATE__.createdAt;
                
                delete window.__ADDAPT_TEMPLATE__;
                
                // Restore jobId and createdAt if they existed
                if (preservedJobId) {
                    window.__ADDAPT_TEMPLATE__ = {
                        jobId: preservedJobId,
                        createdAt: preservedCreatedAt
                    };
                }
            }
        }
        
        // Create personalized greeting with chatbot capabilities
        const greetingContent = `👋 **Hello${userName && userName !== 'there' ? `, ${userName}@` : ''}!** Welcome to ADDAPT AI Assistant.

I'm here to help you create custom data transformations. Here's what I can do:

🔧 **Dataset Generation from template** - Create dataset from your specifications templates\n
📝 **Q&A Flow** - Answer a few questions and I'll generate a dataset template tailored to your needs\n
📂 **Excel Upload** - Upload an Excel file with your data mappings for automatic template creation\n
💬 **Feedback & Iteration** - Refine templates based on your feedback\n
👁️ **Preview & Download** - View and download your generated templates\n

**How would you like to get started?**`;

        const greetingMessage: ChatMessage = {
            id: 'greeting',
            type: 'assistant',
            content: greetingContent,
            timestamp: new Date(),
            status: 'sent'
        };
        
        setLocalMessages([greetingMessage]);
        setWsUiState({
            input_enabled: false,
            input_placeholder: 'Select an option to continue...',
            show_progress: false,
            show_file_upload: false,
            chips: [
                {
                    id: 'answer_questions',
                    text: '📝 Generate a dataset template using a guided workflow',
                    action: 'start_questionnaire'
                },
                {
                    id: 'upload_excel',
                    text: '📂 Upload an excel with instructions ',
                    action: 'upload_excel'
                }
            ],
            progress: undefined,
            show_typing: false,
            clear_previous: false,
            disable_all: false
        });
        
        // Reset question state
        setAllQuestions([]);
        setCurrentQuestionIndex(0);
        setQuestionAnswers({});
        setShowQuestionnaire(false);
        
        // Send reset message to backend to clear server-side state
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            const resetMessage = {
                action: 'reset_session',
                sessionId: newSessionId
            };
            wsRef.current.send(JSON.stringify(resetMessage));
        }
    }, []);

    // Connect to WebSocket for control
    const connectWebSocketControl = useCallback(async () => {
        // Get WebSocket URL from settings
        const settings = window.__ADDAPT_SETTINGS__;
        const wsUrl = settings?.webSocketConfig?.ai || settings?.webSocketConfig?.default;
        
        if (!wsUrl) {
            console.error('No WebSocket URL configured');
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                type: 'system',
                content: '❌ WebSocket configuration not found. Please check your settings.',
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages(prev => [...prev, errorMessage]);
            return;
        }
        
        try {
            // Get JWT token from Amplify
            const { Auth } = await import('aws-amplify');
            const session = await Auth.currentSession();
            
            // Use access token (not ID token) as backend expects access token
            // Access token is for API access, ID token is for identity claims
            const token = session.getAccessToken().getJwtToken();
            
            // Add token as query parameter
            const wsUrlWithToken = `${wsUrl}?token=${token}`;
            
            wsRef.current = new WebSocket(wsUrlWithToken);
            
            wsRef.current.onopen = () => {
                setWsControlConnected(true);
                
                // Reset to clean state and show initial greeting
                resetChat();
            };
            
            wsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleWebSocketMessage(data);
                } catch (error) {
                    console.error('Failed to parse WebSocket message:', error);
                }
            };
            
            wsRef.current.onclose = () => {
                setWsControlConnected(false);
                wsRef.current = null;
            };
            
            wsRef.current.onerror = (error) => {
                console.error('WebSocket control error:', error);
                const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}`,
                    type: 'system',
                    content: '❌ WebSocket connection error. Please refresh the page and try again.',
                    timestamp: new Date(),
                    status: 'sent'
                };
                setLocalMessages(prev => [...prev, errorMessage]);
            };
        } catch (error: any) {
            console.error('Failed to connect WebSocket control:', error);
            let errorContent = '❌ Failed to connect: ';
            
            if (error.message?.includes('No current user')) {
                errorContent += 'Not authenticated. Please sign in first.';
            } else if (error.message?.includes('Network')) {
                errorContent += 'Network error. Please check your connection.';
            } else {
                errorContent += error.message || error;
            }
            
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                type: 'system',
                content: errorContent,
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages(prev => [...prev, errorMessage]);
        }
    }, [handleWebSocketMessage]);

    // Send message via WebSocket
    const sendWebSocketControlMessage = useCallback((action: string, data?: any) => {
        
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            const message = {
                action,
                ...data,
                sessionId: wsSessionId
            };
            wsRef.current.send(JSON.stringify(message));
        } else {
            console.error('WebSocket not connected. State:', wsRef.current?.readyState);
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                type: 'system',
                content: '❌ WebSocket is not connected. Please refresh the page.',
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages(prev => [...prev, errorMessage]);
        }
    }, [wsSessionId]);

    // Disconnect WebSocket
    const disconnectWebSocketControl = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
            setWsControlConnected(false);
        }
    }, []);

    return {
        localMessages,
        setLocalMessages,
        wsUiState,
        setWsUiState,
        wsSessionId,
        wsControlConnected,
        connectWebSocketControl,
        disconnectWebSocketControl,
        sendWebSocketControlMessage,
        resetChat,
        allQuestions,
        setAllQuestions,
        showQuestionnaire,
        setShowQuestionnaire,
        currentQuestionIndex,
        handleQuestionAnswer
    };
};